import React, { useState } from 'react';
import axios from 'axios';
import { Box, Paper, Stack, TextField, Button, Typography, InputAdornment, IconButton } from '@mui/material';
import { LockReset, Visibility, VisibilityOff } from '@mui/icons-material';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

const MIN_PASSWORD_LENGTH = 8;

function ResetPasswordForm({ user, onSuccess }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();

  const validate = () => {
    const temp = {
      password: password.length >= MIN_PASSWORD_LENGTH ? '' : `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      confirmPassword: confirmPassword === password ? '' : 'Passwords do not match.',
    };
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/company-master/users/${user.user_id}/reset-password`,
        { password }
      );
      if (onSuccess) onSuccess(response.data.message);
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
        setTimeout(() => navigate('/'), 1000);
      } else {
        setNotify({ isOpen: true, message: e.response?.data?.message || e.response?.data?.error_message || 'Failed to reset password', type: 'error' });
      }
    }
    setSubmitting(false);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
      <Paper elevation={3} sx={{ width: '420px' }}>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ margin: '30px' }}>
            <Typography variant="body2" color="text.secondary">
              Set a new password for <b>{user?.username}</b> ({user?.email}). Share it with them securely.
            </Typography>
            <TextField
              label="New Password"
              size="small"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((prev) => !prev)} edge="end">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...(errors.password
                ? { error: true, helperText: errors.password }
                : { helperText: `Minimum ${MIN_PASSWORD_LENGTH} characters` })}
            />
            <TextField
              label="Confirm New Password"
              size="small"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              {...(errors.confirmPassword && { error: true, helperText: errors.confirmPassword })}
            />
            <Button
              className="form-btn"
              variant="contained"
              type="submit"
              disabled={submitting}
              endIcon={<LockReset />}
            >
              Reset Password
            </Button>
          </Stack>
        </form>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default ResetPasswordForm;
